import React, { useState } from "react";
import { Card, Row, Col, Divider, Collapse, Tooltip } from "antd";
import {
  SettingOutlined,
  BellOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Text,
  Image,
  SettingsIcon,
  SpeakerPersonIcon,
  WhiteboardIcon,
  BellIcon,
} from "@fluentui/react-northstar";
import { SpinWheelIcon } from "../../../../Assets/icons/icons";
import defaultSchoolLogo from "../../../../Assets/images/DefaultSchoolLogo.png";
import { useTranslation } from "react-i18next";

const SettingsTrustedNetworkCard = ({
  schoolName,
  schoolLogo,
  teacherCount = 0,
  collectionsCount = 0,
  spinWheelsCount = 0,
  onOpenConfig,
  onManageNotifications,
}) => {
  const { t } = useTranslation();
  const translation = t("trustedNetwork").trustedNetworkCard;
  const [logo, setLogo] = useState(() => {
    if (!schoolLogo) return null;
    if (schoolLogo.includes("?")) return schoolLogo;
    const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN?.replace(/"/g, "");
    return sasToken ? schoolLogo + sasToken : schoolLogo;
  });
  const showFallback = !logo;
  const { Panel } = Collapse;

  const customExpandIcon = ({ isActive }) => (
    <DownOutlined
      rotate={isActive ? 180 : 0}
      style={{
        transition: "transform 0.3s ease",
        color: "#6264a7",
        fontSize: "14px",
      }}
    />
  );

  const panelHeader = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: 1,
          minWidth: 0, // Allow text to shrink
        }}
      >
        <div
          style={{
            width: "56px",
            height: "56px",
            border: "1.5px solid #6264a7",
            padding: "3px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            background: showFallback ? "#e6f7ff" : undefined,
          }}
        >
          {logo ? (
            <Image
              src={logo}
              alt={`${schoolName} Logo`}
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
              onError={() => setLogo(null)}
            />
          ) : (
            <div
              style={{
                width: "44px",
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "28px",
                color: "#1890ff",
                background: "#e6f7ff",
                borderRadius: "6px",
                fontWeight: 600,
              }}
            >
              {schoolName?.charAt(0)}
            </div>
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={schoolName} placement="topLeft">
            <div
              style={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "#333",
                overflow: "visible",
                textOverflow: "unset",
                whiteSpace: "normal",
              }}
            >
              {schoolName}
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );

  const optionStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "14px 16px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
    backgroundColor: "#fafafa",
    minHeight: "60px",
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
  };

  const panelContent = (
    <div style={{ padding: "12px 0 0 0" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div
          tabIndex={0}
          role="button"
          aria-label="Open configuration settings"
          onClick={onOpenConfig}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onOpenConfig();
          }}
          style={optionStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f0f4ff";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(98,100,167,0.10)";
            e.currentTarget.style.borderColor = "#6264a7";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#fafafa";
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.borderColor = "#e0e0e0";
          }}
        >
          <SettingOutlined
            style={{ color: "#6264a7", fontSize: "18px", flexShrink: 0 }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Tooltip title="Configuration Settings" placement="topLeft">
              <div
                style={{
                  fontWeight: "500",
                  color: "#333",
                  fontSize: "15px",
                  wordBreak: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Configuration Settings
              </div>
            </Tooltip>
            <Tooltip title="Manage share settings" placement="topLeft">
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  wordBreak: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Manage share settings
              </div>
            </Tooltip>
          </div>
          <RightOutlined style={{ color: "#6264a7", fontSize: "16px" }} />
        </div>
        <div
          tabIndex={0}
          role="button"
          aria-label="Open notification settings"
          onClick={onManageNotifications}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onManageNotifications();
          }}
          style={optionStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f0f4ff";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(40,167,69,0.10)";
            e.currentTarget.style.borderColor = "#28a745";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#fafafa";
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.borderColor = "#e0e0e0";
          }}
        >
          <BellOutlined
            style={{ color: "#28a745", fontSize: "18px", flexShrink: 0 }}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <Tooltip title="Notification Settings" placement="topLeft">
              <div
                style={{
                  fontWeight: "500",
                  color: "#333",
                  fontSize: "15px",
                  wordBreak: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Notification Settings
              </div>
            </Tooltip>
            <Tooltip title="Manage notifications" placement="topLeft">
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  wordBreak: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Manage notifications
              </div>
            </Tooltip>
          </div>
          <RightOutlined style={{ color: "#28a745", fontSize: "16px" }} />
        </div>
      </div>
    </div>
  );

  return (
    <Card
      style={{
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: "1px solid #f0f0f0",
        width: "100%",
        maxWidth: "350px",
        minWidth: "260px",
        overflow: "hidden",
        boxSizing: "border-box",
        margin: "0 auto",
      }}
      hoverable
      bodyStyle={{ 
        padding: "16px",
        "@media (max-width: 480px)": {
          padding: "12px"
        }
      }}
    >
      <Collapse
        ghost
        expandIcon={customExpandIcon}
        style={{
          backgroundColor: "transparent",
          width: "100%",
        }}
        expandIconPosition="end"
      >
        <Panel
          header={panelHeader}
          key="1"
          style={{
            border: "none",
            backgroundColor: "transparent",
            width: "100%",
          }}
        >
          {panelContent}
        </Panel>
      </Collapse>
    </Card>
  );
};

export default SettingsTrustedNetworkCard;
